import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "./store";
import { useLazyGetAllIndustriesQuery } from "../services/industries";
import {
  setIndustries,
  addIndustry,
  updateIndustry,
  removeIndustry,
  selectIndustriesList,
  selectIsIndustriesCacheValid,
  IndustryListItem,
} from "../reducers/industriesSlice";
import { getErrorMessage } from "../types/api";

interface UseIndustriesOptions {
  /** If true, fetches data on mount. Default: true */
  fetchOnMount?: boolean;
}

interface UseIndustriesReturn {
  /** List of industries from Redux store */
  industries: IndustryListItem[];
  /** Whether data is currently being fetched */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether the cache is still valid (< 10 min old) */
  isCacheValid: boolean;
  /** Fetch industries - uses cache if valid, otherwise fetches from API */
  fetchIndustries: () => Promise<void>;
  /** Force refetch from API, ignoring cache */
  refetch: () => Promise<void>;
  /** Add an industry to Redux store */
  addIndustryToStore: (industry: IndustryListItem) => void;
  /** Update an industry in Redux store */
  updateIndustryInStore: (id: string, data: Partial<IndustryListItem>) => void;
  /** Remove an industry from Redux store */
  removeIndustryFromStore: (id: string) => void;
}

/**
 * Hook to manage industries data with Redux caching.
 * Automatically uses cached data if it's less than 10 minutes old.
 */
const useIndustries = (options: UseIndustriesOptions = {}): UseIndustriesReturn => {
  const { fetchOnMount = true } = options;

  const dispatch = useAppDispatch();
  const industries = useAppSelector(selectIndustriesList);
  const isCacheValid = useAppSelector(selectIsIndustriesCacheValid);

  const [getAllIndustriesApi] = useLazyGetAllIndustriesQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetches industries from API and updates Redux store
   */
  const fetchFromApi = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAllIndustriesApi().unwrap();
      if (response?.statusCode === 200) {
        const allData =
          response?.data?.data?.map((data: { _id: string }) => ({
            ...data,
            id: data?._id,
          })) || [];
        dispatch(
          setIndustries({
            list: allData,
            lastFetchedAt: Date.now(),
          })
        );
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to load industries"));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, getAllIndustriesApi]);

  /**
   * Fetch industries - uses cache if valid, otherwise fetches from API
   */
  const fetchIndustries = useCallback(async () => {
    if (isCacheValid && industries.length > 0) {
      return;
    }
    await fetchFromApi();
  }, [isCacheValid, industries.length, fetchFromApi]);

  /**
   * Force refetch from API, ignoring cache
   */
  const refetch = useCallback(async () => {
    await fetchFromApi();
  }, [fetchFromApi]);

  /**
   * Add an industry to Redux store
   */
  const addIndustryToStore = useCallback(
    (industry: IndustryListItem) => {
      dispatch(addIndustry(industry));
    },
    [dispatch]
  );

  /**
   * Update an industry in Redux store
   */
  const updateIndustryInStore = useCallback(
    (id: string, data: Partial<IndustryListItem>) => {
      dispatch(updateIndustry({ id, data }));
    },
    [dispatch]
  );

  /**
   * Remove an industry from Redux store
   */
  const removeIndustryFromStore = useCallback(
    (id: string) => {
      dispatch(removeIndustry(id));
    },
    [dispatch]
  );

  // Fetch on mount if enabled
  useEffect(() => {
    if (fetchOnMount) {
      fetchIndustries();
    }
  }, []);

  return {
    industries,
    isLoading,
    error,
    isCacheValid,
    fetchIndustries,
    refetch,
    addIndustryToStore,
    updateIndustryInStore,
    removeIndustryFromStore,
  };
};

export default useIndustries;
